import { decode } from 'js-base64';
import { Button, Center, Flex, Icon } from '@chakra-ui/react';
import { NotificationCard } from '../../components/Card';
import { NotificationTime } from '../../components/Time';
import { NotificationLink } from '../../components/NotificationLink';
import { MeetingDetailsContainer } from './components';
import { KText } from '@/ui-library/components';
import { Calendar } from '@kula-ai/icons/dual-tone/Calendar';
import { Hackerrank } from '@/assets/icons/ats';
import { useMarkAsReadMutation } from '@/store/query/notification';

// Recruiter RSVP status card. Copy mirrors the email/slack channels: an interviewer RSVP reads
// "<interviewer> has <verb> <interview> with <candidate>"; a candidate RSVP drops the
// "with <candidate>" clause (attendee_is_candidate is sent in custom_attributes by core).
const rsvpVerb = {
  Confirmed: 'accepted',
  Declined: 'declined',
  Tentative: 'tentatively accepted',
};

const InterviewRsvpStatusRecruiter = ({
  notification = {},
  onClose = () => {},
}) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));
  const {
    candidate,
    attendee,
    attendee_is_candidate = false,
    rsvp_status,
    interview_name,
    interview_url,
    interview_location_enum,
    ats_application,
  } = data;
  const { name: candidateName, url } = candidate;
  const contact_id = url.split('/').pop();
  const interview_id = interview_url.split('/').pop();

  // Mirror the other notification buttons: mark read, then open the target in
  // the same tab. "Go to candidate" lands on the candidate's application.
  const handleGoToCandidate = () => {
    markAsRead({ id: notification?.id });
    window.open(ats_application?.url, '_self');
  };

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center
        minW="34px"
        h="34px"
        borderRadius="sm"
        bg={
          interview_location_enum === 'hackerrank' ? 'black' : 'pastel-1-light'
        }
      >
        {interview_location_enum === 'hackerrank' ? (
          <Icon as={Hackerrank} color="ats-primary" />
        ) : (
          <Icon as={Calendar} color="pastel-1-dark" width="16px" />
        )}
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start">
        <KText>
          <KText as="span" size="sm-semi-bold">
            {attendee?.name}
          </KText>
          <KText as="span" size="sm">
            {' '}
            has {rsvpVerb[rsvp_status] || 'responded to'}{' '}
          </KText>
          <KText as="span" size="sm-semi-bold">
            <NotificationLink
              to={`/interviews/${interview_id}`}
              label={interview_name}
              onClose={onClose}
              notification={notification}
            />
          </KText>
          {!attendee_is_candidate && (
            <>
              <KText as="span" size="sm">
                {' '}
                with{' '}
              </KText>
              <KText as="span" size="sm-semi-bold">
                <NotificationLink
                  to={`/candidates/${contact_id}`}
                  label={candidateName}
                  onClose={onClose}
                  notification={notification}
                />
              </KText>
            </>
          )}
        </KText>
        <MeetingDetailsContainer data={data} skipLocation skipWhen />
        {ats_application?.url && (
          <Button
            variant="primary"
            size="sm"
            w="fit-content"
            onClick={handleGoToCandidate}
            data-test-id="go-to-candidate-button"
          >
            Go to candidate
          </Button>
        )}
        <NotificationTime notification={notification} />
      </Flex>
    </Flex>
  );
};

export const InterviewRsvpStatusRecruiterNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <InterviewRsvpStatusRecruiter
        notification={notification}
        onClose={onClose}
      />
    </NotificationCard>
  );
};
